/**
 * Converts survey database to CSV format compatible with pandas processing
 * Based on Python code from SurveyLoader
 */

/**
 * Parse JSONL database (if backend returns that format)
 * @param {string} jsonlText - JSONL text with one JSON per line
 * @returns {Array} Array of response objects
 */
export function parseJSONL(jsonlText) {
    return jsonlText
        .split('\n')
        .filter(line => line.trim())
        .map(line => JSON.parse(line));
}

function getSurveySection(surveyMetadata, preferredLang) {
    if (!surveyMetadata || typeof surveyMetadata !== 'object') return {};
    const get = (lang) => surveyMetadata?.[lang];
    if (preferredLang && get(preferredLang)?.questionnaire) return get(preferredLang);
    if (get('en')?.questionnaire) return get('en');
    if (get('pl')?.questionnaire) return get('pl');
    const fallback = Object.values(surveyMetadata).find(
        (v) => v && Array.isArray(v.questionnaire)
    );
    return fallback || {};
}

function addIndexedHeaders(target, qId, count, suffix = "") {
    for (let i = 1; i <= count; i++) {
        target.push(`${qId}-${i}${suffix}`);
    }
}

function addQuestionHeaders(target, q, maxChoicesForQuestion) {
    const qId = q.questionID;
    const qType = q.questionType;
    const hasExtras = (q.details?.options?.length || 0) > 0;

    if (qType === "Label") return;

    if (qType === "SC") {
        target.push(qId, `${qId}-text`);
        return;
    }
    if (qType === "MC") {
        const optCount = q.details?.options?.length || 0;
        addIndexedHeaders(target, qId, optCount);
        addIndexedHeaders(target, qId, optCount, "-text");
        return;
    }
    if (qType === "Open") {
        target.push(qId, `${qId}-selected`, `${qId}-text`);
        return;
    }
    if (qType === "Ladder") {
        target.push(qId, `${qId}-skipped`, `${qId}-other`, `${qId}-text`);
        return;
    }
    if (qType === "OpenList") {
        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];
        const openListOptCount = selectOptions.length > 0
            ? selectOptions.length
            : (maxChoicesForQuestion[qId] || 0);
        addIndexedHeaders(target, qId, openListOptCount);
        if (hasExtras) {
            target.push(`${qId}-selected`, `${qId}-text`);
        }
        return;
    }
    if (qType === "SelectText") {
        target.push(qId);
        return;
    }

    target.push(qId);
}

function appendEmptyAnswer(row, q, maxChoicesForQuestion) {
    const qId = q.questionID;
    const qType = q.questionType;
    const hasExtras = (q.details?.options?.length || 0) > 0;

    if (qType === "SC") {
        row.push("", "");
        return;
    }
    if (qType === "MC") {
        const optCount = q.details?.options?.length || 0;
        for (let i = 0; i < optCount * 2; i++) row.push("");
        return;
    }
    if (qType === "Open") {
        row.push("", "", "");
        return;
    }
    if (qType === "Ladder") {
        row.push("", "", "", "");
        return;
    }
    if (qType === "OpenList") {
        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];
        const openListOptCount = selectOptions.length > 0
            ? selectOptions.length
            : (maxChoicesForQuestion[qId] || 0);
        if (selectOptions.length > 0) {
            for (let i = 0; i < openListOptCount; i++) row.push("0");
        } else {
            for (let i = 0; i < openListOptCount; i++) row.push("");
        }
        if (hasExtras) {
            row.push("", "");
        }
        return;
    }
    if (qType === "SelectText") {
        row.push("");
        return;
    }

    row.push("");
}

function appendAnswerRow(row, q, answer, maxChoicesForQuestion) {
    const qId = String(q.questionID);
    const qType = q.questionType;
    const value = answer.value;
    const details = value.details || {};
    const hasExtras = (q.details?.options?.length || 0) > 0;

    if (qType === "SC") {
        const choice = Array.isArray(value.choice) ? value.choice[0] : value.choice;
        const text = details[String(choice)] || "";
        row.push(escapeCsvValue(choice), escapeCsvValue(text));
        return;
    }
    if (qType === "MC") {
        const choices = Array.isArray(value.choice) ? value.choice : [value.choice];
        const optCount = q.details?.options?.length || 0;
        const textFields = q.details?.textFields || [];

        for (let i = 1; i <= optCount; i++) {
            row.push(choices.includes(i) || choices.includes(String(i)) ? "1" : "");
        }
        for (let i = 1; i <= optCount; i++) {
            const text = textFields.includes(i) && (choices.includes(i) || choices.includes(String(i)))
                ? (details[String(i)] || "")
                : "";
            row.push(escapeCsvValue(text));
        }
        return;
    }
    if (qType === "Open") {
        const choice = value.choice || "";
        const selected = Array.isArray(details.selected) && details.selected.length
            ? details.selected[0]
            : "";
        const text = details.texts?.[String(selected)] || "";
        row.push(escapeCsvValue(choice), escapeCsvValue(selected), escapeCsvValue(text));
        return;
    }
    if (qType === "Ladder") {
        const choice = value.choice || "";
        const skipped = details.skip ? "1" : "";
        const other = details.other ? "1" : "";
        const text = details.text || "";
        row.push(escapeCsvValue(choice), escapeCsvValue(skipped), escapeCsvValue(other), escapeCsvValue(text));
        return;
    }
    if (qType === "OpenList") {
        const choices = Array.isArray(value.choice) ? value.choice : (value.choice ? [value.choice] : []);
        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];
        const openListOptCount = selectOptions.length > 0
            ? selectOptions.length
            : (maxChoicesForQuestion[qId] || 0);
        const hasSelectOptions = selectOptions.length > 0;

        if (hasSelectOptions) {
            for (let i = 0; i < openListOptCount; i++) {
                const opt = selectOptions[i];
                const optVal = typeof opt === "object" ? opt.value : opt;
                const isSelected = choices.some((choice) => String(choice) === String(optVal));
                row.push(isSelected ? "1" : "0");
            }
        } else {
            for (let i = 0; i < openListOptCount; i++) {
                row.push(escapeCsvValue(choices[i] || ""));
            }
        }
        if (hasExtras) {
            const selected = Array.isArray(details.selected) && details.selected.length
                ? details.selected[0]
                : "";
            const text = details.texts?.[String(selected)] || "";
            row.push(escapeCsvValue(selected), escapeCsvValue(text));
        }
        return;
    }
    if (qType === "SelectText") {
        const selectedValue = value.choice || "";
        row.push(escapeCsvValue(selectedValue));
        return;
    }

    row.push(escapeCsvValue(value.choice || ""));
}

/**
 * Convert database to CSV rows
 * @param {Object|Array} database - Database from fetchDatabase (dict or array)
 * @param {Object} surveyMetadata - Survey metadata with questionnaire
 * @returns {string} CSV content
 */
function toResponsesArray(database) {
    return Array.isArray(database)
        ? database
        : Object.entries(database || {}).map(([id, response]) => ({ id, ...response }));
}

function computeOpenListMaxChoices(questionnaire, responses) {
    const maxChoicesForQuestion = {};
    const responseList = Array.isArray(responses) ? responses : [];

    questionnaire.forEach(q => {
        const qId = q.questionID;
        const qType = q.questionType;

        if (qType !== 'OpenList') return;

        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];
        if (selectOptions.length > 0) {
            maxChoicesForQuestion[qId] = selectOptions.length;
            return;
        }

        let maxChoices = 0;
        responseList.forEach(response => {
            const answers = response.answers || response.data?.answers || {};
            const answer = answers[qId];
            if (answer && answer.value) {
                const choices = Array.isArray(answer.value.choice)
                    ? answer.value.choice
                    : (answer.value.choice ? [answer.value.choice] : []);
                maxChoices = Math.max(maxChoices, choices.length);
            }
        });

        maxChoicesForQuestion[qId] = maxChoices;
    });

    return maxChoicesForQuestion;
}

export function databaseToCSV(database, surveyMetadata, preferredLang) {
    // Convert dict to array if needed
    const responses = toResponsesArray(database);

    if (!responses.length) return '';

    // Get questionnaire from metadata to determine columns
    const section = getSurveySection(surveyMetadata, preferredLang);
    const questionnaire = section?.questionnaire || [];

    // Calculate max choices for OpenList and SelectText (for free text scenarios)
    const maxChoicesForQuestion = computeOpenListMaxChoices(questionnaire, responses);

    // Build CSV headers
    const baseHeaders = ['UserID', 'SurveyID', 'IP', 'Language', 'StartedAt', 'CompletedAt'];
    const questionHeaders = [];

    questionnaire.forEach((q) => addQuestionHeaders(questionHeaders, q, maxChoicesForQuestion));

    const headers = [...baseHeaders, ...questionHeaders];

    // Build CSV rows
    const rows = responses.map(response => {
        const data = response.data || response;
        const answers = response.answers || data.answers || {};

        const row = [
            escapeCsvValue(data.user === 'anonymous' ? response.id : data.user),
            escapeCsvValue(response.survey || data.surveyId),
            escapeCsvValue(response.ip || data.ip || ''),
            escapeCsvValue(response.lang || data.lang),
            escapeCsvValue(data.startedAt || ''),
            escapeCsvValue(data.completedAt || ''),
        ];

        // Process each question
        questionnaire.forEach(q => {
            const qId = String(q.questionID);
            const qType = q.questionType;
            const answer = answers[qId];

            if (qType === 'Label') return;

            if (!answer || !answer.value) {
                appendEmptyAnswer(row, q, maxChoicesForQuestion);
                return;
            }

            appendAnswerRow(row, q, answer, maxChoicesForQuestion);
        });

        return row;
    });

    // Build CSV string
    const csvLines = [
        headers.map(h => escapeCsvValue(h)).join(','),
        ...rows.map(row => row.join(','))
    ];

    return csvLines.join('\n');
}

/**
 * Escape CSV value (handle quotes, commas, newlines)
 */
function escapeCsvValue(value) {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Output filename
 */
export function downloadCSV(csvContent, filename = 'survey-responses.csv') {
    const blob = new Blob(['\uFEFF' + csvContent], {
        type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/**
 * Generate answers labels JSON (mapping answer values to labels)
 * @param {Object} surveyMetadata - Survey metadata with questionnaire
 * @returns {Object} Answer labels mapping
 */
export function generateAnswersLabels(surveyMetadata, preferredLang) {
    const section = getSurveySection(surveyMetadata, preferredLang);
    const questionnaire = section?.questionnaire || [];
    const answersLabels = {};

    const mapOptionLabel = (target, opt, idx) => {
        if (typeof opt === "object" && opt !== null) {
            const label = opt.label ?? opt.value ?? "";
            const value = opt.value;
            target[idx + 1] = label;
            if (value !== undefined && value !== null && value !== "") {
                target[String(value)] = label;
            }
            return;
        }
        target[idx + 1] = opt;
    };

    questionnaire.forEach((q) => {
        const qId = q.questionID;
        const qType = q.questionType;
        const options = q.details?.options || [];
        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];

        if (qType === "Label") return;

        answersLabels[qId] = {};

        const hasOptionLabels =
            (qType === "SC" ||
                qType === "MC" ||
                qType === "Open" ||
                qType === "SelectText" ||
                qType === "OpenList") &&
            options.length > 0;

        if (hasOptionLabels) {
            options.forEach((opt, idx) => {
                mapOptionLabel(answersLabels[qId], opt, idx);
            });
        }

        if (qType === "OpenList" && Array.isArray(selectOptions) && selectOptions.length > 0) {
            selectOptions.forEach((opt, idx) => {
                mapOptionLabel(answersLabels[qId], opt, idx);
            });
        }
    });

    return answersLabels;
}

/**
 * Generate columns labels JSON (mapping column names to question content)
 * @param {Object} surveyMetadata - Survey metadata with questionnaire
 * @returns {Object} Column labels mapping
 */
export function generateColumnsLabels(surveyMetadata, preferredLang, database) {
    const section = getSurveySection(surveyMetadata, preferredLang);
    const questionnaire = section?.questionnaire || [];
    const responses = toResponsesArray(database);
    const maxChoicesForQuestion = computeOpenListMaxChoices(questionnaire, responses);
    const columnsLabels = {};

    const labelFromOption = (opt, fallback) => {
        if (typeof opt === 'object' && opt !== null) {
            return opt.label ?? opt.value ?? fallback;
        }
        return opt ?? fallback;
    };

    questionnaire.forEach(q => {
        const qId = q.questionID;
        const qType = q.questionType;
        const qContent = q.questionContent;
        const options = q.details?.options || [];
        const selectOptions = Array.isArray(q.details?.selectOptions) ? q.details.selectOptions : [];
        const hasExtras = options.length > 0;

        if (qType === 'Label') return;

        if (qType === 'SC') {
            columnsLabels[qId] = qContent;
            return;
        }
        if (qType === 'MC') {
            options.forEach((opt, idx) => {
                const label = labelFromOption(opt, `${qContent} (${idx + 1})`);
                columnsLabels[`${qId}-${idx + 1}`] = label;
            });
            return;
        }
        if (qType === 'Open') {
            columnsLabels[qId] = qContent;
            return;
        }
        if (qType === 'Ladder') {
            columnsLabels[qId] = qContent;
            columnsLabels[`${qId}-skipped`] = `${qContent} (skipped)`;
            columnsLabels[`${qId}-other`] = `${qContent} (other)`;
            return;
        }
        if (qType === 'OpenList') {
            const openListOptCount = selectOptions.length > 0
                ? selectOptions.length
                : (maxChoicesForQuestion[qId] || 0);
            if (selectOptions.length > 0) {
                selectOptions.forEach((opt, idx) => {
                    const label = labelFromOption(opt, `${qContent} (${idx + 1})`);
                    columnsLabels[`${qId}-${idx + 1}`] = label;
                });
            } else {
                for (let i = 1; i <= openListOptCount; i++) {
                    columnsLabels[`${qId}-${i}`] = `${qContent} (${i})`;
                }
            }
            return;
        }
        if (qType === 'SelectText') {
            columnsLabels[qId] = qContent;
            return;
        }

        columnsLabels[qId] = qContent;
    });

    return columnsLabels;
}

/**
 * Create ZIP file with CSV and JSON files
 * @param {string} csvContent - CSV content
 * @param {Object} answersLabels - Answers labels
 * @param {Object} columnsLabels - Columns labels
 * @param {string} surveyId - Survey ID for filenames
 * @returns {Promise<Blob>} ZIP file blob
 */
export async function createExportZip(csvContent, answersLabels, columnsLabels, surveyId) {
    // Dynamically import JSZip
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Add CSV file
    zip.file(`${surveyId}.csv`, '\uFEFF' + csvContent);

    // Add answers JSON
    zip.file(`${surveyId}_answers.json`, JSON.stringify(answersLabels, null, 2));

    // Add columns JSON
    zip.file(`${surveyId}_columns.json`, JSON.stringify(columnsLabels, null, 2));

    // Generate ZIP blob
    return await zip.generateAsync({ type: 'blob' });
}

/**
 * Download ZIP file
 * @param {Blob} zipBlob - ZIP file blob
 * @param {string} filename - Output filename
 */
export function downloadZip(zipBlob, filename = 'survey-export.zip') {
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
